import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Container,
  Backdrop,
  CircularProgress,
  Typography,
  Toolbar,
  Stack,
  Button,
  Paper,
  Grid,
  Link,
  Breadcrumbs,
  Badge,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyIcon from '@mui/icons-material/ContentCopy';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router';
import { useUser } from '@/providers';

import {
  fetchCollections,
  fetchCollection,
  deleteCollection,
  abortExportCollection,
} from '../../actions/collections';
import STATUSES from '../statuses';
import PollEntity from '../shared/PollEntity';
import toNiceTimestamp from '../../util/toNiceTimestamp';
import { clearErrorMessage } from '../../actions/errorMessage';
import ErrorSnackBar from '../shared/ErrorSnackBar';
import THIRD_PARTY_TYPES from '../jobs/JobForm/thirdPartyTypes';
import ConnectDialog from '../shared/ConnectDialog';
import CollectionCopyModal from './CollectionCopyModal';
import CollectionExportModal from './CollectionExportModal';
import toTitleCase from '../../util/toTitleCase';
import ENTITY_TYPES from '@/components/shared/entityTypes';
import readableBytes from '../../util/readableBytes';

export default function CollectionsDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const entity_type = location.pathname.split('/')[1];

  const api_error = useSelector((state) => state.errorMessage);
  const collections = useSelector((state) => state[`${entity_type}s`]);
  const user = useUser();
  const [selectedCollections, setSelectedCollections] = useState([]);
  
  // Convert array to DataGrid v8 format: { type: 'include', ids: Set }
  const rowSelectionModel = useMemo(() => {
    if (!selectedCollections || selectedCollections.length === 0) {
      return { type: 'include', ids: new Set() };
    }
    return { type: 'include', ids: new Set(selectedCollections) };
  }, [selectedCollections]);
  const [transitioningCollections, setTransitioningCollections] = useState([]);

  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectId, setConnectId] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    if (collections === null) {
      setLoading(true);
      if (user?.selected_project) {
        dispatch(fetchCollections(user.selected_project, entity_type));
      }
    } else {
      // Once collections is an array (even if empty), loading is complete
      setLoading(false);
    }
  }, [dispatch, collections, entity_type, user?.selected_project]);

  const removeCollection = useCallback(
    async (id) => {
      setTransitioningCollections((prevTransitioningCollections) => [
        ...prevTransitioningCollections,
        id,
      ]);
      if (user?.selected_project) {
        await dispatch(deleteCollection(user.selected_project, entity_type, id));
      }
      setTransitioningCollections((prevTransitioningCollections) =>
        prevTransitioningCollections.filter(
          (transitioning_id) => transitioning_id !== id,
        ),
      );
    },
    [dispatch, entity_type, user?.selected_project],
  );

  const deleteSelectedCollections = useCallback(
    async (selectedCollections) => {
      if (!user?.selected_project) return;
      const ids = [...selectedCollections];
      setSelectedCollections([]);
      setTransitioningCollections((prevTransitioningCollections) => [
        ...prevTransitioningCollections,
        ...ids,
      ]);
      for (let i = 0; i < ids.length; i++) {
        // parallel actions put a lot of stress on the API
        await dispatch(deleteCollection(user.selected_project, entity_type, ids[i]));
      }
      setTransitioningCollections((prevTransitioningCollections) =>
        prevTransitioningCollections.filter((id) => !ids.includes(id)),
      );
    },
    [dispatch, entity_type, user?.selected_project],
  );

  const renderStatus = useCallback(
    (params) => {
      if (transitioningCollections.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      }
      switch (params.row.status) {
        case STATUSES.READY:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CheckCircleIcon color='success' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.FAILED:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <ErrorOutlineIcon color='error' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        default:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CircularProgress size={20} />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
              <PollEntity
                poll_function={() =>
                  user?.selected_project && params.row.id ? fetchCollection(user.selected_project, entity_type, params.row.id) : null
                }
                interval={10000}
              />
            </Box>
          );
      }
    },
    [transitioningCollections, entity_type, user?.selected_project],
  );

  const renderName = useCallback(
    (params) => {
      return (
        <Link
          component={RouterLink}
          to={`/${entity_type}/details/${params.id}`}
        >
          {params.row.name}
        </Link>
      );
    },
    [entity_type],
  );

  const renderCreatedDate = useCallback((params) => {
    return toNiceTimestamp(params.row.createdAt, true);
  }, []);

  const renderSize = useCallback(
    (params) => {
      return entity_type === ENTITY_TYPES.VOLUME
        ? params.row.capacity
        : readableBytes(params.row.used_size);
    },
    [entity_type],
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 2,
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        minWidth: 150,
        flex: 0.25,
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        type: 'string',
        renderCell: renderCreatedDate,
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: entity_type === ENTITY_TYPES.VOLUME ? 'capacity' : 'size',
        headerName: entity_type === ENTITY_TYPES.VOLUME ? 'Capacity' : 'Size',
        type: 'string',
        renderCell: renderSize,
        minWidth: 50,
        flex: 0.25,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => {
          const actions = [];
          if (
            (params.row.source_type === THIRD_PARTY_TYPES.LOCAL.value &&
              params.row.status === STATUSES.DOWNLOADING &&
              !params.row.size) ||
            (params.row.output_type === THIRD_PARTY_TYPES.LOCAL.value &&
              params.row.status === STATUSES.EXPORTING)
          ) {
            const client_connected =
              params.row.vpn &&
              params.row.vpn.client &&
              params.row.vpn.client.connected;
            actions.push(
              <GridActionsCellItem
                icon={
                  <Badge color='brand' badgeContent={client_connected ? 0 : 1}>
                    <LinkOutlinedIcon />
                  </Badge>
                }
                label='Connect'
                onClick={() => {
                  setConnectId(params.id);
                  setConnectOpen(true);
                }}
              />,
              <GridActionsCellItem
                icon={
                  <Badge color='brand' badgeContent={client_connected ? 0 : 1}>
                    <LinkOutlinedIcon />
                  </Badge>
                }
                label='Connect'
                onClick={() => {
                  setConnectId(params.id);
                  setConnectOpen(true);
                }}
                showInMenu
              />,
            );
          }
          if (params.row.status === STATUSES.EXPORTING) {
            actions.push(
              <GridActionsCellItem
                icon={<CancelOutlinedIcon />}
                label='Cancel Export'
                onClick={() => {
                  if (user?.selected_project) {
                    dispatch(abortExportCollection(user.selected_project, entity_type, params.id));
                  }
                }}
                showInMenu
              />,
            );
          }
          actions.push(
            <GridActionsCellItem
              icon={<FeedOutlinedIcon />}
              label='View Logs'
              onClick={() => {
                navigate(`/${entity_type}/logs/${params.id}`);
              }}
              showInMenu
            />,
            // <GridActionsCellItem
            //   icon={<QueryStatsIcon />}
            //   label='Show Telemetry Info'
            //   onClick={() => setTelemetryInfoOpen(true)}
            //   showInMenu
            // />,
            // <GridActionsCellItem
            //   icon={<UpdateIcon />}
            //   label='Update Stats'
            //   onClick={updateNodeStatistics(params.row)}
            //   showInMenu
            // />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label='Remove'
              onClick={() => removeCollection(params.id)}
              showInMenu
            />,
          );
          return actions;
        },
      },
    ],
    [
      renderStatus,
      renderCreatedDate,
      renderSize,
      navigate,
      renderName,
      removeCollection,
      dispatch,
      entity_type,
      user?.selected_project,
      //   viewDetails,
    ],
  );

  if (loading) {
    return (
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>
    );
  }

  return (
    <Container>
          <Breadcrumbs aria-label='breadcrumb'>
            <Link
              underline='hover'
              color='inherit'
              component={RouterLink}
              to={`/dashboard`}
            >
              Home
            </Link>
            <Typography color='text.primary'>
              {toTitleCase(entity_type)}s
            </Typography>
          </Breadcrumbs>
          <br />
          <Paper sx={{ display: 'flex', width: '100%' }}>
            <Grid container spacing={4} sx={{ flexGrow: 1 }}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
                  <Toolbar>
                    <Stack spacing={2} direction='row' alignItems='center'>
                      <Typography variant='h4'>
                        {toTitleCase(entity_type)}s
                      </Typography>
                      <Button
                        color='brand'
                        onClick={() => navigate(`/${entity_type}/edit`)}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <AddCircleOutlineIcon />
                          <Typography variant='button' display='block'>
                            Create
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={() => setCopyOpen(true)}
                        disabled={Boolean(selectedCollections.length !== 1)}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <CopyIcon />
                          <Typography variant='button' display='block'>
                            Copy
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={() =>
                          navigate(`/${entity_type}/edit?id=${selectedCollections[0]}`)
                        }
                        disabled={Boolean(selectedCollections.length !== 1)}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <EditIcon />
                          <Typography variant='button' display='block'>
                            Edit
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={() => setExportOpen(true)}
                        disabled={Boolean(selectedCollections.length !== 1)}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <FileDownloadOutlinedIcon />
                          <Typography variant='button' display='block'>
                            Export
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={() => {
                          deleteSelectedCollections(selectedCollections);
                        }}
                        disabled={!Boolean(selectedCollections.length)}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <DeleteIcon />
                          <Typography variant='button' display='block'>
                            Delete
                          </Typography>
                        </Stack>
                      </Button>
                    </Stack>
                  </Toolbar>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ flexGrow: 1 }}>
                      <DataGrid
                        autoHeight
                        checkboxSelection
                        getRowId={(row) => row.id}
                        columns={columns}
                        rows={collections || []}
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={(newSelectionModel) => {
                          // DataGrid v8 returns object format: { type: 'include'|'exclude', ids: Set }
                          if (newSelectionModel && typeof newSelectionModel === 'object' && newSelectionModel.ids instanceof Set) {
                            if (newSelectionModel.type === 'include') {
                              setSelectedCollections(Array.from(newSelectionModel.ids));
                            } else {
                              // exclude mode: all rows except those in ids
                              const excludedIds = Array.from(newSelectionModel.ids);
                              setSelectedCollections(
                                (collections || [])
                                  .map((collection) => collection.id)
                                  .filter((id) => !excludedIds.includes(id))
                              );
                            }
                          } else {
                            setSelectedCollections([]);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </Paper>
          <ErrorSnackBar message={api_error} />
          <ConnectDialog
            open={connectOpen}
            handleClose={() => setConnectOpen(false)}
            type={entity_type}
            id={connectId}
          />
          <CollectionCopyModal
            entity_type={entity_type}
            open={copyOpen}
            handleClose={() => setCopyOpen(false)}
            collection={
              collections
                ? collections.find(
                    (collection) => collection.id === selectedCollections[0],
                  )
                : undefined
            }
          />
          <CollectionExportModal
            entity_type={entity_type}
            open={exportOpen}
            handleClose={() => setExportOpen(false)}
            collection={
              collections
                ? collections.find(
                    (collection) => collection.id === selectedCollections[0],
                  )
                : undefined
            }
          />
        </Container>
  );
}
